import { Lightning, Colors, AppData, Router } from '@lightningjs/sdk';
import { getFontFaceFromStyle } from 'support/textUtils';
import { translate } from 'support/translate';
import { LaunchPromptData } from 'types/api/config';
import Button from 'components/common/buttons/Button';
import ActionsList from 'components/common/ActionsList';
import { HoverableComponent } from 'components/common/HoverableComponent';
import { constants } from 'aliases';
import { RoutePath } from 'config/routes';
import { shouldShowLogin } from 'support/routerUtils';
import TextLabel from 'components/common/TextLabel';

const TITLE_MARGIN_BOTTOM = 30;
const DESCRIPTION_MARGIN_BOTTOM = 60;

const TITLE_FONT_SIZE = 63;
const DEFAULT_FONT_SIZE = 32;

const MIN_BUTTON_WIDTH = 300;

export interface AppLaunchTemplateSpec
  extends Lightning.Component.TemplateSpec {
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  Actions: typeof ActionsList;
}

export default class AppLaunch
  extends HoverableComponent<AppLaunchTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<AppLaunchTemplateSpec>
{
  private _Title = this.getByRef('Title')!;
  private _Description = this.getByRef('Description')!;
  private _Actions = this.getByRef('Actions')!;

  private _CancelButton = this._Actions.children[0]! as Button;
  private _AgreeButton = this._Actions.children[1]! as Button;

  private static _termsUrl = '';
  private static _promptData?: LaunchPromptData;

  get announce() {
    const title = this._Title.text?.text ?? '';
    const description = this._Description.text?.text ?? '';

    return [title, description];
  }

  static set termsUrl(url: string) {
    AppLaunch._termsUrl = url;
  }

  static set promptData(data: LaunchPromptData) {
    AppLaunch._promptData = data;
  }

  static override _template(): Lightning.Component.Template<AppLaunchTemplateSpec> {
    return {
      alpha: 0,
      rect: true,
      w: 1920,
      h: 1080,
      color: Colors('modalBackground').get(),
      flex: {
        direction: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: TITLE_MARGIN_BOTTOM },
        text: {
          fontSize: TITLE_FONT_SIZE,
          fontFace: getFontFaceFromStyle('bold'),
        },
      },
      Description: {
        type: TextLabel,
        w: 1880,
        h: 80,
        flexItem: { marginBottom: DESCRIPTION_MARGIN_BOTTOM },
        text: { fontSize: DEFAULT_FONT_SIZE },
      },
      Actions: {
        type: ActionsList,
        signals: {
          $cancel: true,
          $agree: true,
        },
        index: 1,
        items: [
          {
            type: Button,
            minWidth: MIN_BUTTON_WIDTH,
            action: '$cancel',
            passSignals: { $cancel: true },
            signals: {
              $onHover: '$onHover',
            },
            zIndex: constants.ui.zIndex.modalLayer + 1,
          },
          {
            type: Button,
            minWidth: MIN_BUTTON_WIDTH,
            action: '$agree',
            passSignals: { $agree: true },
            signals: {
              $onHover: '$onHover',
            },
            zIndex: constants.ui.zIndex.modalLayer + 1,
          },
        ],
      },
      zIndex: constants.ui.zIndex.modalLayer,
    };
  }

  override _handleBack() {
    this.focusExitModal();
  }

  override _handleKey() {
    // Do not allow the user to bypass the app launch screen
    return true;
  }

  override _getFocused() {
    return this._Actions;
  }

  override _focus() {
    this.update();
  }

  hide() {
    this.patch({ alpha: 0 });
  }

  show() {
    this.patch({ alpha: 1 });
  }

  private $cancel() {
    this.focusExitModal();
  }

  private $agree() {
    const currentTime = new Date().getTime();
    AppData!.storageService.termsAcceptedTimestamp.set(currentTime);

    if (shouldShowLogin() && Router.getActiveRoute() !== RoutePath.LOGIN) {
      Router.navigate(RoutePath.LOGIN);
    } else {
      AppData?.modalService.close();
    }
  }

  private focusExitModal() {
    AppData?.modalService.open('ExitModal');
  }

  private update() {
    const { title, description, accept_title, decline_title } =
      AppLaunch._promptData ?? {};
    const termsUrl = AppLaunch._termsUrl;

    this._Title.patch({ text: { text: title || translate('') } });
    this._Description.patch({
      flex: {
        justifyContent: 'center',
      },
      text: {
        text: `${description} ${termsUrl}` || translate(''),
        textAlign: 'left',
        wordWrapWidth: 1880,
        maxLines: 2,
      },
    });

    this._CancelButton.patch({
      label: decline_title || translate('global.cancelButton'),
    });
    this._AgreeButton.patch({
      label: accept_title || translate('global.agreeButton'),
    });
  }
}
